/**
 * Vendor Sign-In — email/password login for vendor portal. Google login
 * is offered too (uses the shared @react-oauth/google popup, then hits
 * /api/vendor/auth/google-login).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import AuthShell from "@/components/AuthShell";
import { vendorLogin, vendorGoogleLogin } from "@/lib/vendorApi";
import { toast } from "sonner";

export default function VendorSignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await vendorLogin({ email: email.trim().toLowerCase(), password });
      toast.success(`Welcome back, ${res?.vendor?.name || "partner"}.`);
      navigate("/vendor/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async (credResp) => {
    const id_token = credResp?.credential;
    if (!id_token) return toast.error("Google didn't return a credential.");
    setSubmitting(true);
    try {
      const res = await vendorGoogleLogin(id_token);
      toast.success(`Welcome back, ${res?.vendor?.name || "partner"}.`);
      navigate("/vendor/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Google sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Partner sign in"
      subtitle="Track your referrals, subscriptions and commissions in one place."
      footer={
        <span>
          New here?{" "}
          <Link to="/vendor/signup" data-testid="vendor-signin-signup-link" className="text-[#D4AF37] hover:text-[#F3CE5A]">
            Apply to become a partner →
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="vendor-signin-form">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1.5 mb-1.5">
            <Mail className="w-4 h-4" /> Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            data-testid="vendor-signin-email"
            className="w-full bg-black/40 border border-white/10 focus:border-[#D4AF37]/60 focus:outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1.5 mb-1.5">
            <Lock className="w-4 h-4" /> Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="vendor-signin-password"
            className="w-full bg-black/40 border border-white/10 focus:border-[#D4AF37]/60 focus:outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30"
          />
        </label>

        {error && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="vendor-signin-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="vendor-signin-submit"
          className="w-full mt-2 rounded-full bg-gradient-to-r from-[#F3CE5A] to-[#D4AF37] hover:from-[#F5D875] hover:to-[#E4C048] disabled:opacity-40 text-black font-medium py-3 text-sm tracking-wide transition-all shadow-[0_0_20px_rgba(212,175,55,0.28)] flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Sign in
        </button>
      </form>

      <div className="relative my-6 flex items-center gap-3 text-white/30 text-[10px] uppercase tracking-widest">
        <div className="flex-1 h-px bg-white/8" />
        or
        <div className="flex-1 h-px bg-white/8" />
      </div>

      <div className="flex justify-center" data-testid="vendor-signin-google-wrap">
        <GoogleLogin
          onSuccess={onGoogle}
          onError={() => toast.error("Google sign-in failed. Try again.")}
          theme="filled_black"
          shape="pill"
          size="large"
          text="signin_with"
          width="320"
        />
      </div>
    </AuthShell>
  );
}
